import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { EventCardComponent } from "@shared/components/cards/event-card/event-card.component";
import { AuthService } from "@services/auth.service";
import { EventsManagementService } from "@services/events-management.service";
import { EventInfoResponseBody } from "@services/types/events-management-response.type";
import { defer, map, startWith } from "rxjs";
import { SkeletonModule } from "primeng/skeleton";

function sortEventsByStartDateDesc(
  events: EventInfoResponseBody[],
): EventInfoResponseBody[] {
  return [...events].sort(
    (a, b) =>
      new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
  );
}

export type SellerAssignedEventsViewModel =
  | { state: "loading" }
  | { state: "empty" }
  | { state: "ready"; events: EventInfoResponseBody[] };

@Component({
  selector: "app-seller-assigned-events-list",
  imports: [CommonModule, EventCardComponent, SkeletonModule],
  templateUrl: "./seller-assigned-events-list.component.html",
  styleUrl: "./seller-assigned-events-list.component.scss",
})
export class SellerAssignedEventsListComponent {
  private readonly authService = inject(AuthService);
  private readonly eventsManagementService = inject(EventsManagementService);

  readonly viewModel$ = defer(() =>
    this.eventsManagementService.getMyAssignedEvents(0, 250).pipe(
      map(({ data }) => {
        const sellerEventIds = this.getSellerEventIds();
        const sellerEvents = data.filter((event) =>
          sellerEventIds.has(Number(event.id)),
        );

        return sellerEvents.length === 0
          ? ({ state: "empty" } as SellerAssignedEventsViewModel)
          : ({
              state: "ready",
              events: sortEventsByStartDateDesc(sellerEvents),
            } as SellerAssignedEventsViewModel);
      }),
    ),
  ).pipe(startWith<SellerAssignedEventsViewModel>({ state: "loading" }));

  private getSellerEventIds(): Set<number> {
    const assignments =
      this.authService.getLocalStorageUserInfo()?.seller_assignments ?? [];

    return new Set(
      assignments
        .map((assignment) => {
          if (typeof assignment === "number") {
            return assignment;
          }

          return assignment.resource_type === "Event"
            ? assignment.resource_id
            : Number.NaN;
        })
        .filter((eventId) => Number.isFinite(eventId)),
    );
  }
}
